import type { Bar } from './types'

// Typed event bus. Decouples the headless ReplayEngine from the chart adapter and UI.

export type Handler<T> = (payload: T) => void

export interface EventBus<E extends Record<string, unknown>> {
  on<K extends keyof E>(type: K, handler: Handler<E[K]>): () => void
  off<K extends keyof E>(type: K, handler: Handler<E[K]>): void
  emit<K extends keyof E>(type: K, payload: E[K]): void
  clear(): void
}

export function createBus<E extends Record<string, unknown>>(): EventBus<E> {
  const map = new Map<keyof E, Set<Handler<unknown>>>()

  return {
    on(type, handler) {
      let set = map.get(type)
      if (!set) {
        set = new Set()
        map.set(type, set)
      }
      set.add(handler as Handler<unknown>)
      return () => this.off(type, handler)
    },
    off(type, handler) {
      map.get(type)?.delete(handler as Handler<unknown>)
    },
    emit(type, payload) {
      const set = map.get(type)
      if (!set) return
      // Copy to tolerate handlers that unsubscribe during dispatch.
      for (const h of [...set]) (h as Handler<E[typeof type]>)(payload)
    },
    clear() {
      map.clear()
    },
  }
}

/** Events emitted by the ReplayEngine. */
export type ReplayEvents = {
  loaded: { count: number; startIndex: number }
  'bar:revealed': { index: number; bar: Bar }
  'bar:hidden': { index: number }
  seek: { index: number }
  play: { barsPerSec: number }
  pause: { index: number }
  end: { index: number }
}

export type ReplayBus = EventBus<ReplayEvents>
